import { HsRaycastGeometryType } from '../../../graphic-engine-new/raycast-geometry-type.enum';
import { HsShaderDataType } from '../enums/shader-data-type.enum';
import { HsShaderObjectType } from '../enums/shader-object-type.enum';
import { hsAddVertexUniform } from '../functions/add-vertex-uniform.function';
import { hsGetMatrixUniform } from '../functions/get-matrix-uniform.function';
import { HsDefaultShader } from './default-shader.class';

/** Clipping planes picking honors; a section box needs six. */
export const HS_RAYCAST_MAX_CLIPPING_PLANES: number = 6;

export class HsRaycastShaderData {
  private readonly vertexFields: string[] = [];
  private readonly fragmentFields: string[] = [];

  private readonly vertexFunctions: string[] = [];
  private readonly fragmentFunctions: string[] = [];

  private readonly vertexMain: string[] = [];
  private readonly fragmentMain: string[] = [];

  private readonly shader: HsDefaultShader;

  constructor(shaderType: HsShaderObjectType) {
    this.shader = new HsDefaultShader(shaderType);
  }

  public addDepth(): HsRaycastShaderData {
    this.vertexFields.push('out float v_fragDepth;');
    this.vertexMain.push('v_fragDepth = 1.0 + gl_Position.w;');
    this.fragmentFields.push('uniform float u_logDepthBufFC;');
    this.fragmentFields.push('in float v_fragDepth;');
    this.fragmentMain.push('gl_FragDepth = log2(v_fragDepth) * u_logDepthBufFC * 0.5;');
    return this;
  }

  /**
   * Skips fragments that clipping planes cut away, in the scene shaders' convention (discard where
   * dot(-world, normal) > offset), so picks never land on hidden parts of a section.
   */
  public addClipping(): HsRaycastShaderData {
    this.fragmentFields.push(`uniform vec3 u_clipCameraPosition;
      uniform int u_clippingPlaneCount;
      uniform vec4 u_raycastClippingPlanes[${HS_RAYCAST_MAX_CLIPPING_PLANES}];`);
    this.fragmentMain.push(`vec3 clipWorld = v_point.xyz / v_point.w + u_clipCameraPosition;
      for (int planeIndex = 0; planeIndex < ${HS_RAYCAST_MAX_CLIPPING_PLANES}; planeIndex++) {
        if (planeIndex >= u_clippingPlaneCount) {
          break;
        }
        vec4 plane = u_raycastClippingPlanes[planeIndex];
        if (dot(-clipWorld, plane.xyz) > plane.w) {
          discard;
        }
      }`);
    return this;
  }

  public addPoints(): HsRaycastShaderData {
    this.vertexFields.push('uniform float u_pointSize;');
    this.vertexMain.push('gl_PointSize = u_pointSize;');
    this.fragmentMain.push(`vec2 uv = gl_PointCoord * 2.0 - 1.0;
    float distanceUV = length(uv);
    if(distanceUV > 1.0){
      discard;
    }`);
    return this;
  }

  public makeShader(): HsDefaultShader {
    this.shader.makeVertexCode = () => `#version 300 es
      precision highp float;
  
      in vec3 a_positions;
  
      uniform mat4 u_projectionMatrix;
      uniform vec3 u_cameraPosition;
      uniform int u_id;

      ${hsAddVertexUniform('relativeMatrix', HsShaderDataType.mat4, this.shader.shaderType)}
  
      ${this.vertexFields.join('\n')}
  
      out vec4 v_point;
      flat out int v_id;
  
      ${this.vertexFunctions.join('\n')}

      void main() {
        ${hsGetMatrixUniform('relativeMatrix', HsShaderDataType.mat4, this.shader.shaderType)}
        ${this.shader.shaderType === HsShaderObjectType.collection ? 'relativeMatrix[3].xyz -= u_cameraPosition;' : ''}
        v_point = relativeMatrix * vec4(a_positions, 1.0);
        v_id = u_id${this.shader.shaderType === HsShaderObjectType.collection ? ' + gl_InstanceID' : ''};
        gl_Position = (u_projectionMatrix * relativeMatrix) * vec4(a_positions, 1.0);
        ${this.vertexMain.join('\n')}
      }
      `;

    this.shader.makeFragmentCode = () => `#version 300 es
      precision highp float;

      in vec4 v_point;
      flat in int v_id;

      layout(location = 0) out vec4 fragmentColor;

      ${this.fragmentFields.join('\n')}

      ${this.fragmentFunctions.join('\n')}

      void main() {
        ${this.fragmentMain.join('\n')}
        vec4 intersectionPoint = v_point / v_point.w;
        fragmentColor.xyz = intersectionPoint.xyz;
        fragmentColor.w = float(v_id | (${HsRaycastGeometryType.ColorMesh} << 22));
      }
      `;
    return this.shader;
  }
}
